// Playlist Scoring Algorithm
// Calculates dynamic priority scores for playlist ordering

/**
 * Calculate the priority score for a playlist item
 * @param {Object} item - The playlist item
 * @param {number} item.upvotes - Number of upvotes
 * @param {number} item.downvotes - Number of downvotes
 * @param {boolean} item.hostBoosted - If host has boosted this song
 * @param {boolean} item.playNext - If host marked as play next
 * @param {number} item.addedAt - Timestamp when added to playlist
 * @param {string} item.artist - Artist name
 * @param {Array} previousArtists - Artists of songs before this in queue
 * @returns {number} Priority score
 */
export const calculatePriorityScore = (item) => {
  // Deterministic: votes + host overrides ONLY. The queue must sort the
  // same on every client and on every render — the iOS host player uses
  // this exact formula, so what's displayed is what plays. (A time-decay
  // bonus used to live here; it made the queue visibly reshuffle every
  // minute and could never match the host player's order.)
  let score = 0;
  score += (item.upvotes || 0) * 3;  // +3 per upvote
  score -= (item.downvotes || 0) * 5; // -5 per downvote
  if (item.playNext) {
    score += 200; // Play next gets highest priority
  } else if (item.hostBoosted) {
    score += 100; // Host boost
  }
  return score;
};

// When a song joined the queue (approval path writes `timestamp`,
// direct-add writes `addedAt` — accept either)
const addedTimeMs = (item) => {
  const raw = item.addedAt || item.timestamp;
  const ms = raw ? new Date(raw).getTime() : 0;
  return Number.isFinite(ms) ? ms : 0;
};

/**
 * Build a normalized playlist array from the session object.
 * Votes live at session.votes[songKey][userId] = ±1 on the server, so
 * up/down counts and the current user's own vote are derived here rather
 * than read off the playlist item (where nothing ever writes them).
 * Songs already played are excluded — they're history, not queue.
 * @param {Object} session - The session object from the server
 * @param {string} currentUserId - UID of the current user (for userVote)
 * @returns {Array} Playlist items with firebaseKey, vote counts, and userVote
 */
export const buildPlaylistArray = (session, currentUserId = null) => {
  if (!session?.playlist) return [];

  return Object.entries(session.playlist)
    .filter(([, value]) => value && value.status !== 'played')
    .map(([key, value]) => {
      const songVotes = session.votes?.[key] || {};
      let upvotes = 0;
      let downvotes = 0;
      Object.entries(songVotes).forEach(([uid, vote]) => {
        if (uid.startsWith('_')) return; // skip metadata keys
        if (vote === 1) upvotes += 1;
        else if (vote === -1) downvotes += 1;
      });

      return {
        ...value,
        firebaseKey: key,
        upvotes,
        downvotes,
        userVote: currentUserId ? (songVotes[currentUserId] || 0) : 0,
        type: value.musicService === 'apple' ? 'songs' : value.type
      };
    });
};

/**
 * Sort playlist by priority scores
 * @param {Array} playlist - Array of playlist items
 * @param {string} currentlyPlayingId - Playlist key (or id) of currently playing song
 * @returns {Array} Sorted playlist
 */
export const sortPlaylistByPriority = (playlist, currentlyPlayingId = null) => {
  // Separate currently playing song — match by playlist key first (the
  // canonical identifier), falling back to id for older data shapes
  const isCurrent = (item) =>
    currentlyPlayingId != null &&
    (item.firebaseKey === currentlyPlayingId || item.id === currentlyPlayingId);
  const currentlyPlaying = playlist.find(isCurrent);
  const queueItems = playlist.filter(item => !isCurrent(item));
  
  // Calculate scores and sort with a fully deterministic tiebreak:
  // score desc → added earliest first → key. Equal-score songs must
  // never swap places between renders or between clients.
  const scoredItems = queueItems.map(item => ({
    ...item,
    score: calculatePriorityScore(item)
  }));

  scoredItems.sort((a, b) =>
    b.score - a.score ||
    addedTimeMs(a) - addedTimeMs(b) ||
    String(a.firebaseKey || a.id).localeCompare(String(b.firebaseKey || b.id))
  );
  
  // Add position numbers
  const sortedWithPositions = scoredItems.map((item, index) => ({
    ...item,
    position: index + 1
  }));
  
  // Return with currently playing at top if exists
  if (currentlyPlaying) {
    return [
      { ...currentlyPlaying, position: 0, isPlaying: true },
      ...sortedWithPositions
    ];
  }
  
  return sortedWithPositions;
};

/**
 * Calculate skip progress
 * @param {number} skipVotes - Current skip votes
 * @param {number} totalParticipants - Total participants in session
 * @returns {Object} Skip progress info
 */
export const calculateSkipProgress = (skipVotes, totalParticipants) => {
  // Require 50% of participants to skip (minimum 2 votes)
  const requiredVotes = Math.max(2, Math.ceil(totalParticipants * 0.5));
  const progress = Math.min(skipVotes / requiredVotes, 1);
  
  return {
    current: skipVotes,
    required: requiredVotes,
    progress,
    percentage: Math.round(progress * 100),
    canSkip: skipVotes >= requiredVotes
  };
};

/**
 * Get score color based on value
 * @param {number} score - The priority score
 * @returns {string} Tailwind color class
 */
export const getScoreColor = (score) => {
  if (score > 50) return 'text-green-400';
  if (score > 20) return 'text-green-500';
  if (score > 0) return 'text-gray-400';
  if (score > -10) return 'text-orange-400';
  return 'text-red-400';
};

/**
 * Format score display
 * @param {number} upvotes - Number of upvotes
 * @param {number} downvotes - Number of downvotes
 * @returns {string} Formatted score string
 */
export const formatVoteDisplay = (upvotes = 0, downvotes = 0) => {
  const net = upvotes - downvotes;
  if (net > 0) return `+${net}`;
  return `${net}`;
};